
















































import { Vue, Component, Prop, Ref, Emit, Watch } from "vue-property-decorator";

import { 
  formatProjectEventLogEvent, 
  formatProjectNoteLogEvent,
  isProjectStatusLog,
  ProjectLogType, 
  ProjectEventLog, 
  ProjectNoteLog,
  ProjectStatusLog
} from "@/data/models/ProjectLog";
import { 
  ContactPersonInfo,
  doesProjectTypeIdHaveDrying,
  formatProjectOperationSource, 
  getProjectTypeIdOfProject, 
  isPortalOnlyProject, 
  ProjectDetails, 
  ProjectOperationSource 
} from "@/data/models/ProjectDetails";

import { getRoomUsageOptions, loadRoomUsageOptions } from "@/data/repository/RoomUsageOptionsRepository";
import { UserModule } from "@/store/modules/UserModule";
import { formatDateAndTime, formatJsDate } from "@/utils/DateFormatter";
import { timestampToDateParser } from "@/utils/DateHelper";
import ProjectNoteDialog from "./ProjectNoteDialog.vue";

@Component({
  components: {
    "project-note-dialog": ProjectNoteDialog,
  },
})
export default class ProjectLogItemComponent extends Vue {
  @Prop()
  projectLog!: ProjectEventLog | ProjectNoteLog | ProjectStatusLog;

  @Prop()
  contactPersons!: ContactPersonInfo[];

  @Prop()
  projectDetails!: ProjectDetails;

  @Prop() elevation!: number;
  @Prop({ default: "" }) backgroundStyle!: string;

  @Prop({ default: false })
  isReadOnlyMode!: boolean;

  @Ref("projectNoteDialog")
  projectNoteDialog!: ProjectNoteDialog;

  @Emit("onSuccess")
  onSuccess(message: string) {
    this.$emit("onSuccess", message);
  }

  @Emit("onError")
  onError(message: string) {
    this.$emit("onError", message);
  }

  get isLevel2Log() {
    return this.isProjectEventLog && !isProjectStatusLog(this.projectLog as ProjectEventLog);
  }

  get isProjectNoteLog() {
    return this.projectLog.logType === ProjectLogType.NOTE;
  }

  get isProjectStatusLog() {
    return this.projectLog.logType === ProjectLogType.STATUS;
  }

  get isProjectEventLog() {
    return this.projectLog.logType === ProjectLogType.EVENT;
  }

  get hideContactPersonNumber() {
    return this.contactPersons?.length === 1;
  }

  get projectTypeId() {
    return this.projectDetails.clientData?.projectTypeId ?? getProjectTypeIdOfProject(this.projectDetails);
  }

  get containsDryingInfo() {
    return doesProjectTypeIdHaveDrying(this.projectTypeId);
  }

  /** The Beschreibung version a room usage picked from the list is read against; only a note names a room. */
  get roomUsageOptionsVersion() {
    return this.isProjectNoteLog && (this.projectLog as ProjectNoteLog).targetRoom
      ? this.projectDetails.damagePatternData?.roomInfoConfigVersion ?? null
      : null;
  }

  @Watch("roomUsageOptionsVersion", { immediate: true })
  onRoomUsageOptionsVersionChanged() {
    loadRoomUsageOptions(this.roomUsageOptionsVersion);
  }

  get projectLogDescription() {
    if (this.isProjectNoteLog) {
      const projectStatusLog = this.projectLog as ProjectNoteLog;

      return formatProjectNoteLogEvent(
        this,
        projectStatusLog,
        this.contactPersons,
        this.projectDetails.damagePatternData?.rooms ?? [],
        getRoomUsageOptions(this.roomUsageOptionsVersion),
        this.hideContactPersonNumber
      );
    }
    else if (this.isProjectStatusLog) {
      const projectStatusLog = this.projectLog as ProjectStatusLog;

      return projectStatusLog.content;
    }
    else {
      const projectEventLog = this.projectLog as ProjectEventLog;

      return formatProjectEventLogEvent(
        this,
        projectEventLog,
        isPortalOnlyProject(this.projectDetails),
        this.containsDryingInfo,
        this.projectDetails.clientData.contactPersons.length > 1
      );
    }
  }

  /** Phone layout (below 600 px): the entry becomes a caption line over the full-width text. */
  get isPhone() {
    return this.$vuetify.breakpoint.xsOnly;
  }

  /** The phone row's caption: "05.10. 09:41 · 9542 · IRES Project", skipping what is unknown. */
  get phoneCaption() {
    return [this.projectLogTimestamp, this.projectLogUser, this.projectLogSource]
      .filter((part) => !!part)
      .join(" · ");
  }

  get projectLogSource() {
    return formatProjectOperationSource(
      this,
      this.projectLog.source 
        ? this.projectLog.source as ProjectOperationSource
        : isPortalOnlyProject(this.projectDetails)
          ? ProjectOperationSource.PORTAL
          : ProjectOperationSource.PROJECT_MANAGER_APP
    );
  }

  get projectLogTimestamp() {
    if (this.isProjectStatusLog) {
      const date = timestampToDateParser(this.projectLog.createdBy.recordedAt);

      return date 
        ? formatJsDate(
            date,
            true
          )
        : null;
    }

    return formatDateAndTime(
      this.projectLog.createdBy.recordedAt,
      this.projectLog.createdBy.recordedAt
    );
  }

  get projectLogUser() {
    const nickname = UserModule.userNicknameFromRecordInfo(
      this.projectLog.createdBy
    );

    if (nickname === "CLOUD_FUNCTION") {
      return this.$t("projectOperationSource.cloudFunction").toString();
    }

    return nickname;
  }

  async openNoteDetails() {
    if (!this.isProjectNoteLog) {
      return;
    }

    this.projectNoteDialog.show({
      isNewNote: false,
      projectNoteLog: this.projectLog as ProjectNoteLog
    });
  }
}
